import {
    ItemAddonBehaviour,
    ItemAddonType,
    ItemTrait,
    Language,
    RestApiPriceValueDto,
} from '@order-mage/integration-sdk';
import { MAX_ADDON_NESTING_DEPTH } from '@/api/menu-list/max-addon-nesting-depth';
import type {
    StorefrontAddonDef,
    StorefrontAllergen,
    StorefrontAsset,
    StorefrontPackageBehaviour,
    StorefrontPackageItem,
    StorefrontPrice,
    StorefrontTag,
} from '@/api/menu-list/storefront-menu-snapshot.types';
import { resolveEffectiveAddons } from '@/entities/cart/lib/addons';
import {
    buildPackageItemByIdMap,
    enrichPackageWithPriceableItem,
    isPackageApplicable,
    shouldIncludePackages,
    type PackageItemByIdMap,
} from '@/entities/cart/lib/packages';
import {
    getContainedSinglePickHash,
    getHashFromItemIdMap,
    getEffectivePrices,
    getMemberHash,
    normalizeAddonQty,
} from '@/entities/cart/lib/pricing';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import type { LocalizedString } from '@/shared/model/Language';

export type ResolvablePrice = StorefrontPrice;

export type CartPackageRef = {
    id: string;
    packageItemId: string;
    packageItem?: StorefrontPackageItem;
};

export type CartMenuItem = {
    id: string;
    name: LocalizedString;
    shortDescription?: LocalizedString;
    description?: LocalizedString;
    assets?: StorefrontAsset[];
    prices: StorefrontPrice[];
    trait?: ItemTrait;
    allergens?: StorefrontAllergen[];
    tags?: StorefrontTag[];
    packageBehaviours?: StorefrontPackageBehaviour[];
    packages?: CartPackageRef[];
    packageItemIds?: string[];
    addons?: StorefrontAddonDef[];
    variants?: Array<{
        hash: string;
        variantItemId?: string;
        isDefault?: boolean;
        public?: boolean;
    }>;
    /** item_variant rows on this item. Separate from placement `variants`, which are hashes. */
    variantRows?: Array<{ id: string; itemId: string; isDefault?: boolean }>;
};

export type MenuListsData = {
    items: Record<string, MenuListItemsMapValue>;
    menuLists: Array<{
        items?: Array<{ hash?: string; item?: CartMenuItem }>;
        itemRefs: Array<{ item?: CartMenuItem }>;
    }>;
};

type MenuListItemsMapValue = {
    type?: string;
    isDefault?: boolean;
    parentHash?: string;
    item?: CartMenuItem;
    overrideItem?: { prices?: StorefrontPrice[] };
    hash?: string;
};

export type ItemPricesResult = {
    itemPrice: RestApiPriceValueDto;
    packagePrices: Array<{ packageId: string; priceValue: RestApiPriceValueDto }>;
};

export const getPriceForItem = (
    item: { prices?: Array<ResolvablePrice | null | undefined> | null },
    selectedCountry: Country,
    selectedCurrency: Currency,
): RestApiPriceValueDto | null => resolvePrice(item.prices, selectedCountry, selectedCurrency);

export const getPriceForMenuListItem = (
    menuListItem: {
        overrideItem?: { prices?: StorefrontPrice[] } | null;
        item: { prices?: StorefrontPrice[] | null };
        body?: { prices?: StorefrontPrice[] | null };
    },
    selectedCountry: Country,
    selectedCurrency: Currency,
): RestApiPriceValueDto | null => {
    const effectivePrices = getEffectivePrices(menuListItem);
    return resolvePrice(effectivePrices, selectedCountry, selectedCurrency);
};

const resolvePrice = (
    prices: Array<ResolvablePrice | null | undefined> | null | undefined,
    selectedCountry: Country,
    selectedCurrency: Currency,
): RestApiPriceValueDto | null => {
    let price = prices?.find((p) => p?.country === selectedCountry);
    price ??= prices?.find((p) => p && !p.country);
    if (!price) return null;
    return price.priceGroup.priceValues.find((pv) => pv.currency === selectedCurrency) ?? null;
};

export type GetItemPricesDeps = {
    menuLists: MenuListsData | null | undefined;
    selectedCountry: Country;
    selectedCurrency: Currency;
    deliveryMethodDefinitionId: string | undefined;
    userLanguage: Language;
    /** Optional — reused from computeCartTotals to avoid rebuilding per factory. */
    packageItemById?: PackageItemByIdMap;
};

type PriceableItem = Pick<CartMenuItem, 'id' | 'prices' | 'packages' | 'packageBehaviours'>;

export const createGetItemPrices = (deps: GetItemPricesDeps) => {
    const { menuLists, selectedCountry, selectedCurrency, deliveryMethodDefinitionId, userLanguage } = deps;
    const packageItemById = deps.packageItemById ?? buildPackageItemByIdMap(menuLists?.menuLists);
    const cache = new Map<string, ItemPricesResult | null>();

    return (hashOrItem: string | PriceableItem): ItemPricesResult | null => {
        if (!hashOrItem) return null;
        const cacheKey = typeof hashOrItem === 'string' ? `h:${hashOrItem}` : `i:${hashOrItem.id ?? ''}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey) ?? null;
        }

        const hash = typeof hashOrItem === 'string' ? hashOrItem : undefined;
        let menuListItem = hash && menuLists?.items ? menuLists.items[hash] : null;

        if (menuListItem && menuListItem.type === 'variant' && menuListItem.isDefault === true) {
            const parentEntry = menuListItem.parentHash ? menuLists?.items[menuListItem.parentHash] : null;
            if (parentEntry) menuListItem = parentEntry;
        }

        const item = menuListItem?.item ?? (typeof hashOrItem === 'string' ? null : hashOrItem);
        if (!item) {
            cache.set(cacheKey, null);
            return null;
        }

        const itemPrice = menuListItem
            ? getPriceForMenuListItem(
                  {
                      overrideItem: menuListItem.overrideItem,
                      item: menuListItem.item ?? { prices: [] },
                  },
                  selectedCountry,
                  selectedCurrency,
              )
            : getPriceForItem(item, selectedCountry, selectedCurrency);
        if (!itemPrice) {
            cache.set(cacheKey, null);
            return null;
        }

        const includePackages = shouldIncludePackages(item, deliveryMethodDefinitionId);
        const packagePrices = includePackages
            ? (item.packages ?? [])
                  .map((pkg) => {
                      const enriched = enrichPackageWithPriceableItem(pkg, packageItemById);
                      if (!isPackageApplicable(enriched, deliveryMethodDefinitionId, userLanguage)) {
                          return null;
                      }
                      if (!enriched.packageItem) return null;
                      const priceValue = getPriceForItem(
                          enriched.packageItem,
                          selectedCountry,
                          selectedCurrency,
                      );
                      return priceValue ? { packageId: pkg.id, priceValue } : null;
                  })
                  .filter((pkg): pkg is { packageId: string; priceValue: RestApiPriceValueDto } => !!pkg)
            : [];

        const result = { itemPrice, packagePrices };
        cache.set(cacheKey, result);
        return result;
    };
};

type FormValues = {
    variant?: string;
    addons?: Record<string, string | Record<string, number | boolean>>;
    memberVariants?: Record<string, string | undefined>;
};

const addMemberPrice = (
    total: number,
    getItemPrices: ReturnType<typeof createGetItemPrices>,
    memberItem: { item?: CartMenuItem } | undefined,
    qty: number,
): number => {
    if (!memberItem?.item || qty <= 0) return total;
    const memberPrices = getItemPrices(memberItem.item);
    if (!memberPrices?.itemPrice) return total;
    return total + parseFloat(memberPrices.itemPrice.amount) * qty;
};

const applyAddonSelection = (
    total: number,
    getItemPrices: ReturnType<typeof createGetItemPrices>,
    menuLists: MenuListsData,
    idToHash: ReturnType<typeof getHashFromItemIdMap>,
    addon: StorefrontAddonDef,
    addonValue: string | Record<string, number | boolean>,
): number => {
    if (addon.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
        // The contained member is part of the item; a switch adds the new one, never deducts.
        if (
            typeof addonValue === 'string' &&
            addonValue &&
            addonValue !== getContainedSinglePickHash(addon.members, idToHash)
        ) {
            return addMemberPrice(total, getItemPrices, menuLists.items[addonValue], 1);
        }
        return total;
    }

    if (typeof addonValue !== 'object' || addonValue === null) return total;

    let next = total;
    for (const [memberHash, quantity] of Object.entries(addonValue)) {
        const normalizedQty = normalizeAddonQty(quantity);
        if (normalizedQty <= 0) continue;
        const member = addon.members.find((m) => getMemberHash(m, idToHash) === memberHash);
        const baseQty = Number(member?.amount?.value ?? 0);
        const additionalQty = Math.max(0, normalizedQty - baseQty);
        next = addMemberPrice(next, getItemPrices, menuLists.items[memberHash], additionalQty);
    }
    return next;
};

const pricedAmount = (prices: ItemPricesResult | null | undefined): number => {
    if (!prices?.itemPrice) return 0;
    const item = parseFloat(prices.itemPrice.amount) || 0;
    const packages = prices.packagePrices.reduce(
        (sum, pkg) => sum + (parseFloat(pkg.priceValue.amount) || 0),
        0,
    );
    return item + packages;
};

/**
 * Difference between a member variant and the member, including packages. The default
 * variant is empty, its price sits on the member itself.
 */
export const variantPriceDelta = (
    getItemPrices: ReturnType<typeof createGetItemPrices>,
    selectedHash: string | undefined,
    memberHash: string | undefined,
): number =>
    pricedAmount(selectedHash ? getItemPrices(selectedHash) : null) -
    pricedAmount(memberHash ? getItemPrices(memberHash) : null);

export const createGetItemFinalPrice = (
    menuLists: MenuListsData | null | undefined,
    getItemPrices: ReturnType<typeof createGetItemPrices>,
) => {
    const idToHash = getHashFromItemIdMap(menuLists?.items);

    return (
        formValues: FormValues,
        hash: string,
        addons?: StorefrontAddonDef[] | null,
    ): { amount: string; currency: string } | null => {
        if (!menuLists?.items) return null;

        const selectedVariantHash = formValues.variant || hash;
        const variantItem = menuLists.items[selectedVariantHash];
        if (!variantItem) return null;

        const variantPrices = getItemPrices(selectedVariantHash);
        if (!variantPrices) return null;

        let total = parseFloat(variantPrices.itemPrice.amount);
        const effectiveAddons = resolveEffectiveAddons(menuLists.items, addons, formValues.variant, hash);
        if (!formValues.addons || !effectiveAddons) {
            return { amount: total.toString(), currency: variantPrices.itemPrice.currency };
        }

        const selectedHashes = (
            addon: StorefrontAddonDef,
            addonValue: string | Record<string, number | boolean>,
        ): string[] => {
            if (addon.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
                return typeof addonValue === 'string' && addonValue ? [addonValue] : [];
            }
            if (typeof addonValue !== 'object' || addonValue === null) return [];
            return Object.entries(addonValue)
                .filter(([, quantity]) => normalizeAddonQty(quantity) > 0)
                .map(([memberHash]) => memberHash);
        };

        const priceTree = (defs: StorefrontAddonDef[] | undefined, depth: number, path: Set<string>) => {
            if (!defs?.length || depth >= MAX_ADDON_NESTING_DEPTH) return;
            for (const addon of defs) {
                if (addon.type === ItemAddonType.GROUP) {
                    priceTree(addon.groupMembers, depth, path);
                    continue;
                }
                let addonValue = formValues.addons?.[addon.id];
                // A nested section the cart never wrote still keeps its contained members, so their
                // children stay priced. The root keeps today's rule: a missing key skips the section.
                if ((addonValue === undefined || addonValue === null) && depth > 0) {
                    if (addon.behaviour === ItemAddonBehaviour.SINGLE_PICK) {
                        const contained = getContainedSinglePickHash(addon.members, idToHash);
                        addonValue = contained || undefined;
                    } else {
                        const kept = Object.fromEntries(
                            addon.members.flatMap((member) => {
                                const amount = Number(member.amount?.value ?? 0);
                                const memberHash = getMemberHash(member, idToHash);
                                return amount > 0 && memberHash ? [[memberHash, amount] as const] : [];
                            }),
                        );
                        addonValue = Object.keys(kept).length ? kept : undefined;
                    }
                }
                if (!addonValue) continue;
                total = applyAddonSelection(total, getItemPrices, menuLists, idToHash, addon, addonValue);
                for (const memberHash of selectedHashes(addon, addonValue)) {
                    const memberItem = menuLists.items[memberHash]?.item;
                    if (!memberItem?.id || path.has(memberItem.id)) continue;
                    const member = addon.members.find((row) => getMemberHash(row, idToHash) === memberHash);
                    const variantRow = member?.itemId
                        ? memberItem.variantRows?.find(
                              (row) => row.id === formValues.memberVariants?.[member.itemId],
                          )
                        : undefined;
                    const variantHash =
                        variantRow && !variantRow.isDefault ? idToHash.get(variantRow.itemId) : undefined;
                    const variantItem = variantHash ? menuLists.items[variantHash]?.item : undefined;
                    if (variantHash) {
                        const quantity =
                            addon.behaviour === ItemAddonBehaviour.SINGLE_PICK
                                ? 1
                                : typeof addonValue === 'object'
                                  ? normalizeAddonQty(addonValue[memberHash])
                                  : 0;
                        const selectedAmount = Number(getItemPrices(variantHash)?.itemPrice.amount ?? 0);
                        // The default variant is empty, its price sits on the member itself.
                        const memberAmount = Number(getItemPrices(memberHash)?.itemPrice.amount ?? 0);
                        total += (selectedAmount - memberAmount) * quantity;
                    }
                    const nested = variantItem?.addons?.length ? variantItem.addons : memberItem.addons;
                    const nextPath = new Set(path);
                    nextPath.add(memberItem.id);
                    if (variantRow && !variantRow.isDefault) nextPath.add(variantRow.itemId);
                    priceTree(nested, depth + 1, nextPath);
                }
            }
        };

        priceTree(effectiveAddons, 0, new Set());

        return { amount: total.toString(), currency: variantPrices.itemPrice.currency };
    };
};
